import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLiveTick } from '@/lib/liveContent';
import { ArrowRight, Search, X } from 'lucide-react';
import {
  bylineFor,
  getPublishedArticles,
  INSIGHT_TOPICS,
  topicFor,
} from '@/data/insights';
import { InsightCard } from './InsightCard';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 6;

const matchesQuery = (art, q) => {
  const topicLabels = [art.primaryTopic, ...art.secondaryTopics]
    .map((k) => topicFor(k)?.label)
    .filter(Boolean);
  const haystack = [
    art.title,
    art.excerpt,
    ...topicLabels,
    bylineFor(art),
    ...(art.keywords || []),
    ...(art.tags || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q.split(/\s+/).every((term) => haystack.includes(term));
};

// Sections 3 + 4 of the Insights hub: "Explore by topic" navigation with
// keyword search, then the "Latest insights" results grid. One data-driven
// grid — no hard-coded per-filter layouts. Filters sync with ?topic= so
// business-challenge links and future topic pages deep-link cleanly.
export const TopicExplorer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [topic, setTopic] = useState(() => searchParams.get('topic') || 'all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  const tick = useLiveTick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const articles = useMemo(() => getPublishedArticles(), [tick]);

  // React to inbound deep links (e.g. /insights?topic=people from the
  // business-challenge section or a future topic landing page).
  useEffect(() => {
    const t = searchParams.get('topic') || 'all';
    setTopic((prev) => (prev === t ? prev : t));
    const p = parseInt(searchParams.get('page') || '1', 10);
    const safe = Number.isFinite(p) && p > 0 ? p : 1;
    setPage((prev) => (prev === safe ? prev : safe));
  }, [searchParams]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((art) => {
      const topicMatch =
        topic === 'all' || art.primaryTopic === topic || art.secondaryTopics.includes(topic);
      return topicMatch && (!q || matchesQuery(art, q));
    });
  }, [articles, topic, query]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;
  const isFiltered = topic !== 'all' || query.trim() !== '';

  const selectTopic = (key) => {
    setTopic(key);
    setPage(1);
    // Keep the URL honest and shareable without a page reload.
    const next = new URLSearchParams(searchParams);
    if (key === 'all') next.delete('topic');
    else next.set('topic', key);
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setTopic('all');
    setQuery('');
    setPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('topic');
    next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };

  return (
    <section aria-labelledby="explore-topics-heading" data-testid="insights-topic-explorer" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div id="explore-topics" className="scroll-mt-28">
          <div className="max-w-2xl">
            <p className="flex items-center gap-3">
              <span className="font-mono text-xs font-medium tracking-widest text-gold-700">02</span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Topics</span>
            </p>
            <h2 id="explore-topics-heading" data-testid="explore-topics-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
              Explore by topic
            </h2>
          </div>

          <div className="mt-8 flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter insights by topic">
              {[{ key: 'all', label: 'All Insights' }, ...INSIGHT_TOPICS].map((t) => {
                const count =
                  t.key === 'all'
                    ? articles.length
                    : articles.filter(
                        (a) => a.primaryTopic === t.key || a.secondaryTopics.includes(t.key)
                      ).length;
                const isActive = topic === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    aria-pressed={isActive}
                    data-testid={`insights-topic-${t.key}`}
                    onClick={() => selectTopic(t.key)}
                    className={cn(
                      'rounded-full border-2 px-4 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                      !isActive && 'border-slate-200 bg-white text-navy-800 hover:border-navy-300 hover:bg-navy-50'
                    )}
                    style={
                      isActive
                        ? {
                            borderColor: t.color || '#0B1729',
                            backgroundColor: t.soft || '#E2E8F2',
                            color: t.dark || '#0B1729',
                          }
                        : undefined
                    }
                  >
                    {t.label}
                    <span
                      className={cn('ml-1.5 text-xs font-semibold', isActive ? 'opacity-70' : 'text-slate-400')}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative w-full xl:max-w-xs">
              <label htmlFor="insights-search" className="sr-only">
                Search insights by keyword
              </label>
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                id="insights-search"
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search title, topic or keyword…"
                data-testid="insights-search"
                className="h-11 w-full rounded-md border border-slate-300 bg-white pl-10 pr-9 text-sm text-navy-900 shadow-sm transition-colors placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  data-testid="insights-search-clear"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition-colors hover:text-navy-900"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div id="latest-insights" className="mt-14 scroll-mt-28">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h2 data-testid="latest-insights-heading" className="text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
              Latest insights
            </h2>
            <div className="flex items-center gap-4">
              <p aria-live="polite" className="text-sm font-semibold text-slate-600" data-testid="insights-results-count">
                Showing {visible.length} of {filtered.length} insights
              </p>
              {isFiltered && (
                <button
                  type="button"
                  onClick={clearFilters}
                  data-testid="insights-clear-filters-btn"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
                {visible.map((art, i) => (
                  <InsightCard key={art.id || art.slug} article={art} index={i} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    data-testid="insights-load-more-btn"
                    className="group inline-flex items-center gap-2 rounded-md border-2 border-navy-900 px-7 py-3.5 text-sm font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
                  >
                    Load More Insights
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-300 bg-mist px-6 py-14 text-center" data-testid="insights-empty">
              <p className="text-lg font-extrabold text-navy-900">No matching insights</p>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
                Try a different topic or search term — or clear the filters to browse all insights.
              </p>
              <button
                type="button"
                onClick={clearFilters}
                data-testid="insights-empty-clear-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
